import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  IActivo,
  ICliente,
  ICumplimientoRecorridoVivo,
  IReporteBase,
  IUsuario,
} from 'modelos/src';
import { LoadingService } from '../../../../../../../auxiliares/servicios/loading.service';
import { LoginService } from '../../../../../../login/login.service';
import { FormularioDialogService } from '../../../../../../../auxiliares/servicios/formulario-dialog.service';
import { ParamsService } from '../../../../../../../auxiliares/servicios/params.service';
import { Router } from '@angular/router';
import { HelperService } from '../../../../../../../auxiliares/servicios/helper.service';
import { AsignarRecorridoDialogComponent } from './asignar-recorrido-dialog/asignar-recorrido-dialog.component';
import { ActivosService } from '../../../../../../../auxiliares/servicios/http/activos.service';
import { MapaFacilDialogComponent } from '../../../../../../../auxiliares/mapa-facil-dialog/mapa-facil-dialog.component';
import { MapaFacilData } from '../../../../../../../auxiliares/mapa-facil/interfaces';
import { MapaVehiculosService } from '../../../mapaVehiculos.service';
import { TriperoService } from '../../../../../../../auxiliares/servicios/http/tripero.service';
import { Lista2Service } from '../../../../../../../auxiliares/servicios/lista2.service';
import { CrearReglaVehiculoComponent } from '../../../../vehiculos/crear-regla-vehiculo/crear-regla-vehiculo.component';
import { UsuariosService } from '../../../../../../../auxiliares/servicios/http/usuarios.service';
import { CumplimientosRecorridoService } from '../../../../../../../auxiliares/servicios/http/cumplimientos-recorrido.service';
import { RecorridosDinamicosService } from '../../../../../../../auxiliares/servicios/http/recorridos-dinamicos.service';
import { DialogService } from '../../../../../../../auxiliares/dialog/dialog.service';
import { ResumenesDatosService } from '../../../../../../../auxiliares/servicios/http/resumenes-datos.service';
import {
  queryRendimientoCombustible,
  VistaRendimientoCombustible,
  vistaRendimientoCombustible,
} from '../../../../rendimiento-combustible';
import { etiquetaTipoYFuncion } from '../../../../etiquetas-vehiculo';

@Component({
  selector: 'app-info-vehiculo',
  templateUrl: './info-vehiculo.component.html',
  styleUrl: './info-vehiculo.component.scss',
  standalone: false,
})
export class InfoVehiculoComponent implements OnChanges, OnDestroy {
  loading = inject(LoadingService);
  private formularios = inject(FormularioDialogService);
  private navigator = inject(Router);
  private paramsService = inject(ParamsService);
  triperoService = inject(TriperoService);
  helper = inject(HelperService);
  etiquetaTipoYFuncion = etiquetaTipoYFuncion;
  private mapaService = inject(MapaVehiculosService);
  private activosService = inject(ActivosService);
  private lista2 = inject(Lista2Service);
  private usuariosService = inject(UsuariosService);
  private cumplimientosService = inject(CumplimientosRecorridoService);
  private recorridosDinamicosService = inject(RecorridosDinamicosService);
  private dialogService = inject(DialogService);
  private resumenesDatosService = inject(ResumenesDatosService);

  //Recibidos de la barra lateral
  @Input() public reporte?: IReporteBase<'Tracker 4G'>; //Reporte seleccionado

  //Enviados a la barra lateral
  @Output() public reporteChange = new EventEmitter<
    IReporteBase<'Tracker 4G'>
  >(); //Cambio en el reporte seleccionado
  @Output() public reporteVerChange = new EventEmitter<boolean>(); //Ver detalle del reporte

  public direccion: string = ''; //Dirección del último reporte del vehículo
  public cliente?: ICliente;
  public nivelCombustible: number | undefined;
  public temperatura: number | undefined;
  /** Consumo promedio de 30 días (solo vehículos con sensor de combustible). */
  public rendimiento: VistaRendimientoCombustible | null = null;
  public chofer: IUsuario | null = null;
  // Último idChofer pedido: el detalle del vehículo llega después del listado,
  // así que el disparador no puede ser "cambió el vehículo".
  private idChoferCargado?: string;
  public showChoferViewer = false;

  // Subs de combustible/temperatura: se cancelan al cambiar de vehículo para
  // evitar que la respuesta de un vehículo anterior pise la del actual (race).
  private subCombustible?: Subscription;
  private subTemperatura?: Subscription;

  /** Medición del recorrido en curso. `null` = el vehículo no está midiendo. */
  public cumplimiento: ICumplimientoRecorridoVivo | null = null;

  get tieneRecorridoAsignado(): boolean {
    return !!this.reporte?.activo?.vehiculo?.idRecorrido;
  }

  get idRecorridoDinamicoAsignado(): string | undefined {
    return this.reporte?.activo?.vehiculo?.idRecorridoDinamico;
  }

  /** Color de la barra de progreso según qué tan lejos está de completar. */
  get colorCumplimiento(): 'primary' | 'accent' | 'warn' {
    if (!this.cumplimiento) return 'primary';
    if (this.cumplimiento.estado === 'Desviado') return 'warn';
    return this.cumplimiento.estado === 'Completado' ? 'primary' : 'accent';
  }

  /**
   * Nombre del recorrido que está haciendo. Sale de la lista que el mapa ya tiene
   * cargada, así que no hace falta pedirlo: el reporte no trae el recorrido
   * populado (el populate del mapa sólo trae `vehiculo`, no `vehiculo.recorrido`).
   */
  get nombreRecorrido(): string | undefined {
    const id =
      this.cumplimiento?.idRecorrido ??
      this.reporte?.activo?.vehiculo?.idRecorrido;
    if (!id) return undefined;
    return this.mapaService.recorridos.find((r) => r._id === id)?.nombre;
  }

  get kmCubiertos(): number {
    return (this.cumplimiento?.metrosCubiertos ?? 0) / 1000;
  }

  get kmTotales(): number {
    return (this.cumplimiento?.metrosTotales ?? 0) / 1000;
  }

  constructor() {
    this.cliente = LoginService.getCliente();
  }

  //FUNCIONES PARA INTERACCIÓN CON LA BARRA LATERAL:
  //Cerrar el detalle del vehículo
  public close() {
    this.reporte = undefined;
    this.reporteChange.emit(this.reporte);
  }

  //Ver detalles del reporte
  public reporteVer() {
    this.reporteVerChange.emit(true);
  }

  //FUNCIONES PARA INTERACCIÓN CON OTROS COMPONENTES
  //Editar vehículo
  public async editar(vehiculo: IActivo) {
    this.paramsService.setParams({ vehiculo });
    this.navigator.navigate(['main/vehiculos/vehiculos/editar']);
  }

  //Crear regla de evento para el vehículo
  public crearRegla(): void {
    if (!this.reporte?.activo) return;
    CrearReglaVehiculoComponent.open(this.formularios, {
      activo: this.reporte.activo,
    });
  }

  public puedeCrearRegla(): boolean {
    return this.helper.tienePermiso(
      'Administración - Crear eventos personalizados',
    );
  }

  //Asignar recorrido al vehículo
  public async asignarRecorrido() {
    if (!this.reporte?.activo) {
      return;
    }

    const dialogRef = this.formularios.abrir(AsignarRecorridoDialogComponent, {
      tamano: 'grande',
      data: {
        vehiculo: this.reporte.activo,
      },
    });

    dialogRef.afterClosed().subscribe(async () => {
      await this.refrescarRecorridoAsignado();
    });
  }

  /**
   * Refresca `reporte` con los datos más recientes del servicio y publica el
   * recorrido (traza/dinámico) asignado al mapa — se llama después de
   * cualquier acción que pueda haber cambiado la asignación (asignar
   * recorrido, finalizar un recorrido dinámico).
   */
  private async refrescarRecorridoAsignado(): Promise<void> {
    // Forzar actualización de reportes después del cambio
    this.mapaService.refrescarReportes();

    const idActivo = this.reporte?.activo?._id;
    if (!idActivo) return;

    // Esperar un momento para que el backend procese el cambio
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Buscar el reporte actualizado en la lista de reportes del servicio
    const reportes = this.mapaService.reportes;
    const reporteActualizado = reportes.find((r) => r.activo?._id === idActivo);
    if (!reporteActualizado) return;

    this.reporte = reporteActualizado;
    this.reporteChange.emit(this.reporte);

    const idRecorrido = reporteActualizado.activo?.vehiculo?.idRecorrido;
    const idRecorridoDinamico =
      reporteActualizado.activo?.vehiculo?.idRecorridoDinamico;
    this.mapaService.updateSeleccionadoBarraLateral({
      idRecorridoAsignado: idRecorrido,
      idRecorridoDinamicoAsignado: idRecorridoDinamico,
    });
    // Cambió el recorrido: la medición anterior ya no aplica.
    await this.cargarCumplimiento();
  }

  /**
   * Da por terminado el recorrido dinámico asignado ahora mismo: calcula y
   * persiste el cumplimiento definitivo, y libera al vehículo para poder
   * asignarle otro sin esperar a que pase el tiempo ni borrar este.
   */
  public async finalizarRecorridoDinamico(): Promise<void> {
    const id = this.idRecorridoDinamicoAsignado;
    if (!id) return;
    const confirmado = await this.dialogService.confirm(
      'Finalizar recorrido dinámico',
      '¿Dar por terminado el recorrido dinámico asignado a este vehículo? Se calcula el cumplimiento final y el vehículo queda libre para asignarle otro.',
      'Finalizar',
      'Cancelar',
    );
    if (!confirmado) return;
    try {
      await this.recorridosDinamicosService.finalizar(id);
      this.helper.notifSuccess('Recorrido dinámico finalizado');
      await this.refrescarRecorridoAsignado();
    } catch (error) {
      this.helper.notifError(error);
    }
  }

  /**
   * Trae el estado de la medición del recorrido y lo publica al mapa para que
   * pinte la traza cubierta.
   *
   * Se llama en cada reporte nuevo (no sólo al cambiar de vehículo): el
   * porcentaje avanza con cada reporte. No es una entidad de Mongo, así que no
   * llega por websocket; el reporte hace de disparador.
   */
  private async cargarCumplimiento() {
    const idActivo = this.reporte?.activo?._id;
    if (!idActivo || !this.tieneRecorridoAsignado) {
      this.cumplimiento = null;
      this.mapaService.updateSeleccionadoBarraLateral({
        cumplimientoRecorrido: null,
      });
      return;
    }
    try {
      const vivo = await this.cumplimientosService.getVivo(idActivo);
      // Puede haber cambiado el vehículo seleccionado mientras iba el request.
      if (this.reporte?.activo?._id !== idActivo) return;
      this.cumplimiento = vivo;
      this.mapaService.updateSeleccionadoBarraLateral({
        cumplimientoRecorrido: vivo,
      });
    } catch (error) {
      // El panel no muestra nada cuando no hay medición, así que el error sólo
      // deja rastro en consola: sin esto, un 404 de api-gestion sería
      // indistinguible de "todavía no arrancó".
      console.warn(
        `[cumplimiento] no se pudo leer la medición del activo ${idActivo}`,
        error,
      );
      // El recorrido puede no estar midiendo cumplimiento: no es un error a
      // mostrarle al operador.
      this.cumplimiento = null;
    }
  }

  /** Descarta la medición en curso y arranca de nuevo. */
  public async reiniciarCumplimiento() {
    const idActivo = this.reporte?.activo?._id;
    if (!idActivo) return;
    const confirmado = await this.dialogService.confirm(
      'Reiniciar medición',
      '¿Descartar el avance medido de este recorrido? El próximo reporte sobre la traza empieza a medir de nuevo.',
      'Reiniciar',
      'Cancelar',
    );
    if (!confirmado) return;
    try {
      await this.cumplimientosService.reiniciarVivo(idActivo);
      await this.cargarCumplimiento();
      this.helper.notifSuccess('Medición reiniciada');
    } catch (error) {
      this.helper.notifError(error);
    }
  }

  //HOOKS
  async ngOnChanges(changes: SimpleChanges) {
    if (changes['reporte']) {
      // El avance del recorrido cambia con cada reporte, así que se refresca
      // siempre, antes del corte por "mismo vehículo" de abajo.
      void this.cargarCumplimiento();

      // El recorrido asignado vive en activo.vehiculo, que el listado liviano
      // del mapa NO trae: llega recién con el detalle por id, cuando el
      // vehículo YA es el mismo. Por eso se sincroniza acá arriba, antes del
      // corte por "mismo vehículo", y solo cuando cambió el valor (si no,
      // sería una escritura de estado por cada tick del WS).
      const idRecorrido = this.reporte?.activo?.vehiculo?.idRecorrido;
      const idRecorridoDinamico =
        this.reporte?.activo?.vehiculo?.idRecorridoDinamico;
      const seleccionado = this.mapaService.getSeleccionadoActual();
      if (
        seleccionado?.idRecorridoAsignado !== idRecorrido ||
        seleccionado?.idRecorridoDinamicoAsignado !== idRecorridoDinamico
      ) {
        this.mapaService.updateSeleccionadoBarraLateral({
          idRecorridoAsignado: idRecorrido,
          idRecorridoDinamicoAsignado: idRecorridoDinamico,
        });
      }

      const prevId = changes['reporte'].previousValue?.activo?._id;
      const currId = changes['reporte'].currentValue?.activo?._id;

      // El conductor también sale de activo.vehiculo (llega con el detalle por
      // id, no con el listado). Se pide cuando cambia el idChofer, no cuando
      // cambia el vehículo, que es lo mismo salvo en ese primer refresh.
      // idChofer es el _id del Usuario (FK de identidad), así que se trae por
      // GET /usuarios/:id. NO usar getConductoresFiltered: ahora lista la
      // entidad Permiso y filtraría por _id del permiso, no del usuario.
      const idChofer = this.reporte?.activo?.vehiculo?.idChofer;
      if (idChofer !== this.idChoferCargado) {
        this.idChoferCargado = idChofer;
        this.chofer = null;
        if (idChofer) {
          this.usuariosService
            .getById(idChofer)
            .then((usuario) => {
              // Puede haber cambiado el vehículo mientras iba el request.
              if (this.reporte?.activo?.vehiculo?.idChofer !== idChofer) return;
              this.chofer = usuario ?? null;
            })
            .catch(() => {});
        }
      }

      // El @Input cambia en cada update WS (posición/estado), pero combustible/
      // temperatura solo dependen del VEHÍCULO. Salir si es el mismo vehículo
      // evita un storm de requests/escrituras en cada tick (la velocidad y
      // demás valores se reflejan por el binding del template).
      if (prevId === currId) {
        return;
      }

      // Cambió el vehículo: limpiar valores y cancelar requests en vuelo del
      // vehículo anterior. Sin esto se mostraban valores mezclados (nivel viejo
      // + capacidad nueva) hasta que llegara la respuesta, o el response viejo
      // pisaba al nuevo si llegaba más tarde.
      this.nivelCombustible = undefined;
      this.temperatura = undefined;
      this.rendimiento = null;
      this.subCombustible?.unsubscribe();
      this.subTemperatura?.unsubscribe();

      // Buscar el último reporte de combustible para este vehículo
      const idActivo = this.reporte?.activo?._id;
      if (idActivo) {
        this.subCombustible = this.lista2
          .ultimosReportesGenericos<'Tracker 4G Combustible'>(
            {
              filter: JSON.stringify({
                idsAsignados: idActivo,
                tipoReporte: 'Tracker 4G Combustible',
              }),
              sort: '-fechaCreacion',
              limit: 1,
              includeChildren: true,
            },
            0,
          )
          .subscribe((data) => {
            const v = data.datos[0]?.valores as any;
            this.nivelCombustible =
              v?.nivelCombustibleTotal ?? v?.nivelPostVariacion;
          });

        // Desde ultimoreportegenericos, igual que el combustible de acá arriba.
        // Contra `reportesgenericos` el sort por -fechaCreacion no entra en el
        // índice {idsAsignados, tipoReporte, 'valores.timestamp'} y el blocking
        // sort sobre 3 meses de reportes agotaba el maxTimeMS de 45s.
        this.subTemperatura = this.lista2
          .ultimosReportesGenericos<'Tracker 4G Temperatura'>(
            {
              filter: JSON.stringify({
                idsAsignados: idActivo,
                tipoReporte: 'Tracker 4G Temperatura',
              }),
              sort: '-fechaCreacion',
              limit: 1,
              includeChildren: true,
            },
            0,
          )
          .subscribe((data) => {
            this.temperatura = (data.datos[0]?.valores as any)?.temperatura;
          });

        this.cargarRendimiento(idActivo);
      }
    }
  }

  /** El resumen lo recalcula gestion-cron cada hora: no hace falta suscribirse. */
  private async cargarRendimiento(idActivo: string): Promise<void> {
    try {
      const res =
        await this.resumenesDatosService.getFiltered<'Rendimiento Combustible Vehículos'>(
          queryRendimientoCombustible(idActivo),
        );
      // Si mientras tanto se eligió otro vehículo, esta respuesta ya no aplica
      if (this.reporte?.activo?._id !== idActivo) return;
      this.rendimiento = vistaRendimientoCombustible(
        res.datos[0]?.resumen,
        this.reporte?.activo?.vehiculo?.modoConsumo,
      );
    } catch {
      this.rendimiento = null;
    }
  }

  ngOnDestroy(): void {
    this.subCombustible?.unsubscribe();
    this.subTemperatura?.unsubscribe();
  }

  // Método para deseleccionar el vehículo
  public deseleccionar(): void {
    this.reporteChange.emit(undefined);
  }

  public verStreetView(): void {
    const valores = this.reporte?.valores as any;
    const geojson = valores?.snappedGeojson || valores?.geojson;
    if (!geojson?.coordinates) return;

    const [lng, lat] = geojson.coordinates as [number, number];
    const titulo =
      this.helper.extraerDireccion(valores?.direccion) || 'Street View';
    const data: MapaFacilData = {
      markers: [{ coor: [lng, lat], label: 'puntoSimple', centrar: true }],
      vista: 'streetview',
      titulo,
    };
    this.formularios.abrir(MapaFacilDialogComponent, {
      tamano: 'grande',
      data,
      cerrarAfuera: true,
    });
  }
}
